import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, LayoutGrid, Users, Mail, Phone, Plus, Pencil, Trash2 } from 'lucide-react';
import DataTable from '../../components/DataTable';
import BulkImportExport from '../../components/BulkImportExport';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';
import EditableCombobox from '../../components/EditableCombobox';
import SearchSelect from '../../components/SearchSelect';
import { useServerTable } from '../../hooks/useServerTable';
import { downloadBlob } from '../../utils/downloadBlob';
import {
  units as unitsApi,
  blocks as blocksApi,
  residents as residentsApi,
  residentImport,
  societyFeatures,
  type Unit,
  type Block,
  type Resident,
  type ApiError,
  type CsvImportResult,
} from '../../services/api';

interface ContactPolicy {
  requireEmail: boolean;
  requirePhone: boolean;
}

type Tab = 'units' | 'residents';

const inp = {
  width: '100%', padding: '9px 12px', background: 'var(--nx-surface-3)',
  border: '1px solid var(--nx-border)', borderRadius: 8, fontSize: 13,
  color: 'var(--nx-text-primary)', outline: 'none', boxSizing: 'border-box' as const,
};
const lbl = {
  display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)',
  marginBottom: 5, letterSpacing: '0.05em', textTransform: 'uppercase' as const,
};
const actionBtn = {
  background: 'none', border: 'none', cursor: 'pointer',
  padding: '4px 6px', borderRadius: 6, color: 'var(--nx-text-muted)',
  display: 'inline-flex', alignItems: 'center',
};

const statusColor = (s: string) =>
  s === 'occupied'
    ? { bg: 'rgba(26,108,246,0.1)', text: '#1A6CF6' }
    : { bg: 'rgba(16,185,129,0.1)', text: '#10B981' };

const typeColor = (t: string) => {
  switch (t) {
    case 'owner':  return { bg: 'rgba(16,185,129,0.1)',  text: '#10B981' };
    case 'tenant': return { bg: 'rgba(26,108,246,0.1)',  text: '#1A6CF6' };
    default:       return { bg: 'rgba(245,158,11,0.1)',  text: '#D97706' };
  }
};

/* ── Units tab ───────────────────────────────────────────────────────────── */

const UNIT_INIT = { unitNumber: '', blockId: '', floor: '', areaSqft: '', status: 'vacant' };

export const UnitsTab: React.FC<{ societyId: string }> = ({ societyId }) => {
  const {
    rows, totalElements, totalPages, loading,
    page, setPage, pageSize, setPageSize,
    sortField, sortDir, setSort,
    q, setQ, refetch,
  } = useServerTable<Unit>(params => unitsApi.list(societyId, params), [societyId]);

  const [blocks, setBlocks] = useState<Block[]>([]);
  const [blocksLoading, setBlocksLoading] = useState(false);
  const [blocksError, setBlocksError] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Unit | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState(UNIT_INIT);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmUnit, setConfirmUnit] = useState<Unit | null>(null);
  const [deleteError, setDeleteError] = useState('');

  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState<CsvImportResult | null>(null);

  const loadBlocks = () => {
    setBlocksLoading(true);
    setBlocksError(false);
    blocksApi.list(societyId)
      .then(setBlocks)
      .catch(() => setBlocksError(true))
      .finally(() => setBlocksLoading(false));
  };

  useEffect(() => {
    if (!showForm || blocks.length > 0) return;
    loadBlocks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm]);

  const change = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const openAdd = () => { setEditing(null); setFormError(''); setForm(UNIT_INIT); setShowForm(true); };

  const openEdit = (u: Unit) => {
    setEditing(u);
    setFormError('');
    setForm({
      unitNumber: u.unitNumber,
      blockId:    u.blockId    ?? '',
      floor:      u.floor      != null ? String(u.floor)    : '',
      areaSqft:   u.areaSqft   != null ? String(u.areaSqft) : '',
      status:     u.status,
    });
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditing(null); setForm(UNIT_INIT); setFormError(''); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setFormError('');
    const payload = {
      unitNumber: form.unitNumber,
      blockId:    form.blockId || undefined,
      floor:      form.floor    ? Number(form.floor)    : undefined,
      areaSqft:   form.areaSqft ? Number(form.areaSqft) : undefined,
      status:     form.status as Unit['status'],
    };
    try {
      if (editing) {
        await unitsApi.update(societyId, editing.id, payload);
      } else {
        await unitsApi.create(societyId, payload);
      }
      refetch();
      closeForm();
    } catch (err) {
      setFormError((err as ApiError).message || 'Failed to save unit. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (u: Unit) => { setConfirmUnit(u); setDeleteError(''); };

  const confirmDelete = async () => {
    if (!confirmUnit) return;
    setDeletingId(confirmUnit.id);
    try {
      await unitsApi.delete(societyId, confirmUnit.id);
      refetch();
      setConfirmUnit(null);
    } catch { setDeleteError('Failed to delete unit. Please try again.'); }
    finally { setDeletingId(null); }
  };

  const handleImport = async (file: File) => {
    setImporting(true); setImportResult(null);
    try {
      const result = await unitsApi.importCsv(societyId, file);
      setImportResult(result);
      refetch();
    } catch (err) {
      setImportResult({ created: 0, failed: 1, errors: [(err as ApiError).message || 'Import failed'] });
    } finally { setImporting(false); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await unitsApi.exportCsv(societyId);
      downloadBlob(blob, 'units-export.csv');
    } catch { /* ignore */ }
    finally { setExporting(false); }
  };

  const selectedBlock = blocks.find(b => b.id === form.blockId);
  const floorOptions = useMemo(() => {
    if (!selectedBlock?.floorCount) return [];
    return Array.from({ length: selectedBlock.floorCount }, (_, i) => {
      const v = String(i + 1);
      return { value: v, label: v };
    });
  }, [selectedBlock]);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>
          {totalElements} unit{totalElements !== 1 ? 's' : ''}
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <BulkImportExport onImport={handleImport} onExport={handleExport} importing={importing} exporting={exporting} />
          <button onClick={openAdd} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
            <Plus size={14} /> Add Unit
          </button>
        </div>
      </div>

      {importResult && (
        <div style={{ padding: '10px 16px', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 10, fontSize: 13, color: '#16A34A', marginBottom: 16 }}>
          Imported {importResult.created} units{importResult.failed > 0 ? `, ${importResult.failed} failed: ${importResult.errors.join('; ')}` : '.'}
        </div>
      )}

      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--nx-border)', overflow: 'hidden' }}>
        <DataTable
          columns={[
            { key: 'unit', header: 'Unit Number', cellStyle: { fontWeight: 500 }, sortable: true, sortKey: 'unitNumber', render: (u: Unit) => u.unitNumber },
            { key: 'block', header: 'Block', cellStyle: { color: 'var(--nx-text-secondary)' }, sortable: true, sortKey: 'b.name', render: (u: Unit) => u.blockName || '—' },
            { key: 'floor', header: 'Floor', cellStyle: { color: 'var(--nx-text-secondary)' }, sortable: true, sortKey: 'floor', render: (u: Unit) => u.floor != null ? `Floor ${u.floor}` : '—' },
            { key: 'area', header: 'Area (sq ft)', cellStyle: { color: 'var(--nx-text-secondary)' }, sortable: true, sortKey: 'areaSqft', render: (u: Unit) => u.areaSqft != null ? u.areaSqft.toLocaleString() : '—' },
            {
              key: 'status', header: 'Status',
              sortable: true, sortKey: 'status',
              render: (u: Unit) => {
                const sc = statusColor(u.status);
                return (
                  <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: sc.bg, color: sc.text }}>
                    {u.status.charAt(0).toUpperCase() + u.status.slice(1)}
                  </span>
                );
              },
            },
            {
              key: 'actions', header: '',
              cellStyle: { textAlign: 'right', whiteSpace: 'nowrap' },
              render: (u: Unit) => (
                <>
                  <button onClick={() => openEdit(u)} style={actionBtn} title="Edit unit">
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(u)}
                    disabled={deletingId === u.id}
                    style={{ ...actionBtn, color: '#EF4444', opacity: deletingId === u.id ? 0.4 : 1 }}
                    title="Delete unit"
                  >
                    <Trash2 size={14} />
                  </button>
                </>
              ),
            },
          ]}
          rows={rows}
          rowKey={u => u.id}
          loading={loading}
          emptyIcon={<LayoutGrid size={32} style={{ color: 'var(--nx-text-muted)' }} />}
          emptyText="No units registered for this society."
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          sortField={sortField}
          sortDir={sortDir}
          onSortChange={setSort}
          searchValue={q}
          onSearchChange={setQ}
          searchPlaceholder="Search units…"
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
        />
      </div>

      <ConfirmDialog
        open={confirmUnit !== null}
        variant="danger"
        title={`Delete unit "${confirmUnit?.unitNumber}"?`}
        message="This unit will be permanently removed. This action cannot be undone."
        confirmLabel="Delete Unit"
        isLoading={deletingId !== null}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => { setConfirmUnit(null); setDeleteError(''); }}
      />

      {showForm && (
        <Modal
          title={editing ? 'Edit Unit' : 'Add Unit'}
          submitLabel={editing ? 'Save Changes' : 'Add Unit'}
          error={formError}
          isLoading={saving}
          onClose={closeForm}
          onSubmit={handleSubmit}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Unit Number *</label>
              <input name="unitNumber" value={form.unitNumber} onChange={change} placeholder="e.g. A-101" required style={inp} />
            </div>
            <div>
              <label style={lbl}>Block</label>
              {blocksLoading ? (
                <div style={{ ...inp, color: 'var(--nx-text-muted)' }}>Loading blocks…</div>
              ) : blocksError ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: '#EF4444' }}>Failed to load.</span>
                  <button type="button" onClick={loadBlocks} style={{ fontSize: 12, color: 'var(--nx-blue)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>Retry</button>
                </div>
              ) : (
                <SearchSelect
                  value={form.blockId}
                  onChange={v => setForm(p => ({ ...p, blockId: v }))}
                  options={blocks.map(b => ({ value: b.id, label: b.name }))}
                  placeholder={blocks.length === 0 ? 'No blocks available' : 'Search blocks…'}
                  disabled={blocks.length === 0}
                />
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Floor</label>
              <EditableCombobox
                name="floor"
                value={form.floor}
                onChange={v => setForm(p => ({ ...p, floor: v }))}
                options={floorOptions}
                placeholder="e.g. 3"
              />
            </div>
            <div>
              <label style={lbl}>Area (sq ft)</label>
              <input type="number" name="areaSqft" value={form.areaSqft} onChange={change} placeholder="e.g. 1200" min="0" style={inp} />
            </div>
          </div>

          <div>
            <label style={lbl}>Status</label>
            <select name="status" value={form.status} onChange={change} style={inp}>
              <option value="vacant">Vacant</option>
              <option value="occupied">Occupied</option>
            </select>
          </div>
        </Modal>
      )}
    </>
  );
};

/* ── Residents tab ───────────────────────────────────────────────────────── */

const RESIDENT_INIT = {
  unitId: '', fullName: '', email: '', phone: '', type: 'owner',
  moveInDate: '', moveOutDate: '',
};

export const ResidentsTab: React.FC<{ societyId: string; contactPolicy?: ContactPolicy | null }> = ({ societyId, contactPolicy: contactPolicyProp }) => {
  const [fetchedPolicy, setFetchedPolicy] = useState<ContactPolicy | null>(null);
  useEffect(() => {
    if (contactPolicyProp !== undefined) return;
    societyFeatures.get(societyId)
      .then(f => setFetchedPolicy({ requireEmail: f.requireResidentEmail, requirePhone: f.requireResidentPhone }))
      .catch(() => {});
  }, [societyId, contactPolicyProp]);
  const contactPolicy = contactPolicyProp !== undefined ? contactPolicyProp : fetchedPolicy;

  const {
    rows, totalElements, totalPages, loading,
    page, setPage, pageSize, setPageSize,
    sortField, sortDir, setSort,
    q, setQ, refetch,
  } = useServerTable<Resident>(params => residentsApi.list(societyId, params), [societyId]);

  const [unitList, setUnitList] = useState<Unit[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitsError, setUnitsError] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Resident | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState(RESIDENT_INIT);
  const [active, setActive] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmResident, setConfirmResident] = useState<Resident | null>(null);
  const [deleteError, setDeleteError] = useState('');

  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState<CsvImportResult | null>(null);

  const loadUnits = () => {
    setUnitsLoading(true);
    setUnitsError(false);
    unitsApi.list(societyId, { status: 'vacant', size: 500 })
      .then(res => setUnitList(res.content))
      .catch(() => setUnitsError(true))
      .finally(() => setUnitsLoading(false));
  };

  useEffect(() => {
    if (!showForm) return;
    loadUnits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm]);

  const unitLabel = (u: Unit) => u.blockName ? `${u.unitNumber} (${u.blockName})` : u.unitNumber;

  // The vacant-units fetch excludes the resident's own current unit (it's occupied by
  // them), so it's added back in directly from the resident record itself — which already
  // carries unitNumber/blockName from the list API — rather than a second network call.
  const unitOptions = useMemo(() => {
    const opts = unitList.map(u => ({ value: u.id, label: unitLabel(u) }));
    if (editing?.unitId && !opts.some(o => o.value === editing.unitId)) {
      opts.unshift({
        value: editing.unitId,
        label: editing.blockName ? `${editing.unitNumber} (${editing.blockName})` : (editing.unitNumber ?? 'Current unit'),
      });
    }
    return opts;
  }, [unitList, editing]);

  const change = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const openAdd = () => { setEditing(null); setFormError(''); setForm(RESIDENT_INIT); setActive(true); setShowForm(true); };

  const openEdit = (r: Resident) => {
    setEditing(r); setFormError('');
    setForm({
      unitId:      r.unitId,
      fullName:    r.fullName,
      email:       r.email       ?? '',
      phone:       r.phone       ?? '',
      type:        r.type,
      moveInDate:  r.moveInDate  ?? '',
      moveOutDate: r.moveOutDate ?? '',
    });
    setActive(r.active);
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditing(null); setForm(RESIDENT_INIT); setFormError(''); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.unitId) { setFormError('Please select a unit.'); return; }
    if (contactPolicy?.requireEmail && !form.email.trim()) { setFormError('Email is required for residents in this society.'); return; }
    if (contactPolicy?.requirePhone && !form.phone.trim()) { setFormError('Phone number is required for residents in this society.'); return; }
    setSaving(true); setFormError('');
    const payload = {
      unitId:      form.unitId,
      fullName:    form.fullName,
      type:        form.type as Resident['type'],
      email:       form.email       || undefined,
      phone:       form.phone       || undefined,
      moveInDate:  form.moveInDate  || undefined,
      moveOutDate: form.moveOutDate || undefined,
      active,
    };
    try {
      if (editing) {
        await residentsApi.update(societyId, editing.id, payload);
      } else {
        await residentsApi.create(societyId, payload);
      }
      refetch();
      closeForm();
    } catch (err) {
      setFormError((err as ApiError).message || 'Failed to save resident. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (r: Resident) => { setConfirmResident(r); setDeleteError(''); };

  const confirmDelete = async () => {
    if (!confirmResident) return;
    setDeletingId(confirmResident.id);
    try {
      await residentsApi.delete(societyId, confirmResident.id);
      refetch();
      setConfirmResident(null);
    } catch { setDeleteError('Failed to delete resident. Please try again.'); }
    finally { setDeletingId(null); }
  };

  const handleImport = async (file: File) => {
    setImporting(true); setImportResult(null);
    try {
      const result = await residentImport.importCsv(societyId, file);
      setImportResult(result);
      refetch();
    } catch (err) {
      setImportResult({ created: 0, failed: 1, errors: [(err as ApiError).message || 'Import failed'] });
    } finally { setImporting(false); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await residentsApi.exportCsv(societyId);
      downloadBlob(blob, 'residents-export.csv');
    } catch { /* ignore */ }
    finally { setExporting(false); }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>
          {totalElements} resident{totalElements !== 1 ? 's' : ''}
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <BulkImportExport onImport={handleImport} onExport={handleExport} importing={importing} exporting={exporting} />
          <button onClick={openAdd} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
            <Plus size={14} /> Add Resident
          </button>
        </div>
      </div>

      {importResult && (
        <div style={{ padding: '10px 16px', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 10, fontSize: 13, color: '#16A34A', marginBottom: 16 }}>
          Imported {importResult.created} residents{importResult.failed > 0 ? `, ${importResult.failed} failed: ${importResult.errors.join('; ')}` : '.'}
        </div>
      )}

      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--nx-border)', overflow: 'hidden' }}>
        <DataTable
          columns={[
            { key: 'name', header: 'Name', cellStyle: { fontWeight: 500 }, sortable: true, sortKey: 'fullName', render: (r: Resident) => r.fullName },
            {
              key: 'contact', header: 'Contact',
              render: (r: Resident) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12 }}>
                  {r.email && <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--nx-text-secondary)' }}><Mail size={11} /> {r.email}</span>}
                  {r.phone && <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--nx-text-secondary)' }}><Phone size={11} /> {r.phone}</span>}
                  {!r.email && !r.phone && <span style={{ color: 'var(--nx-text-muted)' }}>—</span>}
                </div>
              ),
            },
            {
              key: 'type', header: 'Type',
              sortable: true, sortKey: 'type',
              render: (r: Resident) => {
                const tc = typeColor(r.type);
                return (
                  <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: tc.bg, color: tc.text }}>
                    {r.type.charAt(0).toUpperCase() + r.type.slice(1)}
                  </span>
                );
              },
            },
            {
              key: 'active', header: 'Status',
              sortable: true, sortKey: 'active',
              render: (r: Resident) => (
                <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: r.active ? 'rgba(16,185,129,0.1)' : 'rgba(100,116,139,0.1)', color: r.active ? '#10B981' : '#64748B' }}>
                  {r.active ? 'Active' : 'Inactive'}
                </span>
              ),
            },
            { key: 'moveIn', header: 'Move-in', cellStyle: { color: 'var(--nx-text-muted)' }, sortable: true, sortKey: 'moveInDate', render: (r: Resident) => r.moveInDate || '—' },
            {
              key: 'actions', header: '',
              cellStyle: { textAlign: 'right', whiteSpace: 'nowrap' },
              render: (r: Resident) => (
                <>
                  <button onClick={() => openEdit(r)} style={actionBtn} title="Edit resident">
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(r)}
                    disabled={deletingId === r.id}
                    style={{ ...actionBtn, color: '#EF4444', opacity: deletingId === r.id ? 0.4 : 1 }}
                    title="Delete resident"
                  >
                    <Trash2 size={14} />
                  </button>
                </>
              ),
            },
          ]}
          rows={rows}
          rowKey={r => r.id}
          loading={loading}
          emptyIcon={<Users size={32} style={{ color: 'var(--nx-text-muted)' }} />}
          emptyText="No residents registered for this society."
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          sortField={sortField}
          sortDir={sortDir}
          onSortChange={setSort}
          searchValue={q}
          onSearchChange={setQ}
          searchPlaceholder="Search residents…"
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
        />
      </div>

      <ConfirmDialog
        open={confirmResident !== null}
        variant="danger"
        title={`Delete "${confirmResident?.fullName}"?`}
        message="This resident will be permanently removed. This action cannot be undone."
        confirmLabel="Delete Resident"
        isLoading={deletingId !== null}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => { setConfirmResident(null); setDeleteError(''); }}
      />

      {showForm && (
        <Modal
          title={editing ? 'Edit Resident' : 'Add Resident'}
          submitLabel={editing ? 'Save Changes' : 'Add Resident'}
          error={formError}
          isLoading={saving}
          onClose={closeForm}
          onSubmit={handleSubmit}
        >
          <div>
            <label style={lbl}>Full Name *</label>
            <input name="fullName" value={form.fullName} onChange={change} required placeholder="Full name" style={inp} autoFocus />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Unit *</label>
              {unitsLoading ? (
                <div style={{ ...inp, color: 'var(--nx-text-muted)' }}>Loading units…</div>
              ) : unitsError ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: '#EF4444' }}>Failed to load units.</span>
                  <button type="button" onClick={loadUnits} style={{ fontSize: 12, color: 'var(--nx-blue)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>Retry</button>
                </div>
              ) : (
                <SearchSelect
                  value={form.unitId}
                  onChange={v => setForm(p => ({ ...p, unitId: v }))}
                  options={unitOptions}
                  placeholder={unitOptions.length === 0 ? 'No units available' : 'Search units…'}
                  required
                  disabled={unitOptions.length === 0}
                />
              )}
            </div>
            <div>
              <label style={lbl}>Type *</label>
              <select name="type" value={form.type} onChange={change} style={inp}>
                <option value="owner">Owner</option>
                <option value="tenant">Tenant</option>
                <option value="family">Family Member</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Email{contactPolicy?.requireEmail ? ' *' : ''}</label>
              <input type="email" name="email" value={form.email} onChange={change} placeholder="resident@email.com" required={!!contactPolicy?.requireEmail} style={inp} />
            </div>
            <div>
              <label style={lbl}>Phone{contactPolicy?.requirePhone ? ' *' : ''}</label>
              <input name="phone" value={form.phone} onChange={change} placeholder="+91 98765 43210" required={!!contactPolicy?.requirePhone} style={inp} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Move-in Date</label>
              <input type="date" name="moveInDate" value={form.moveInDate} onChange={change} style={inp} />
            </div>
            <div>
              <label style={lbl}>Move-out Date</label>
              <input type="date" name="moveOutDate" value={form.moveOutDate} onChange={change} style={inp} />
            </div>
          </div>
          <div>
            <label style={lbl}>Status</label>
            <select value={active ? 'true' : 'false'} onChange={e => setActive(e.target.value === 'true')} style={inp}>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </Modal>
      )}
    </>
  );
};

/* ── SocietyDataPage ─────────────────────────────────────────────────────── */

const TAB_BTN = (active: boolean): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
  fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-body)',
  color: active ? '#fff' : 'var(--nx-text-secondary)',
  background: active ? 'linear-gradient(135deg, #1A6CF6, #6D28D9)' : 'transparent',
});

const SocietyDataPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('units');

  if (!id) return null;

  return (
    <div style={{ background: '#f8fafc', minHeight: '100%', padding: 32, fontFamily: 'var(--font-body)' }}>
      <button
        onClick={() => navigate(`/admin/societies/${id}`)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--nx-text-secondary)', fontSize: 13, cursor: 'pointer', marginBottom: 20, fontFamily: 'var(--font-body)', padding: 0 }}
      >
        <ArrowLeft size={15} /> Back to Society
      </button>

      <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 800, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
        Apartment Data
      </h1>
      <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', marginBottom: 20 }}>
        Units and residents for this society. Super admins can edit or delete records directly.
      </p>

      <div style={{ display: 'flex', gap: 6, background: '#fff', border: '1px solid var(--nx-border)', borderRadius: 10, padding: 6, width: 'fit-content', marginBottom: 20 }}>
        <button style={TAB_BTN(tab === 'units')} onClick={() => setTab('units')}>
          <LayoutGrid size={14} /> Units
        </button>
        <button style={TAB_BTN(tab === 'residents')} onClick={() => setTab('residents')}>
          <Users size={14} /> Residents
        </button>
      </div>

      {tab === 'units' ? <UnitsTab societyId={id} /> : <ResidentsTab societyId={id} />}
    </div>
  );
};

export default SocietyDataPage;
